import { useTranslation } from "react-i18next";

import style from "./TestimonialMarquee.module.css";
import Icon from "@/ui/icon/Icon";
import { useFeaturedReviews } from "@/features/landing/hooks/useFeaturedReviews";
import { useLocalized } from "@/i18n/useLocalized";

function Stars({ value = 0 }) {
	const { t } = useTranslation();

	return (
		<span
			className={style.stars}
			aria-label={t("landing:testimonials.rating", { value })}
		>
			{[1, 2, 3, 4, 5].map((star) => (
				<Icon
					key={star}
					icon="fa-star"
					iconType={star <= value ? "fa-solid" : "fa-regular"}
				/>
			))}
		</span>
	);
}

function Testimonial({ review }) {
	const { t } = useTranslation();
	const l = useLocalized();

	return (
		<figure className={style.card}>
			<Stars value={review.stars} />

			<blockquote className={style.quote}>
				{t("landing:testimonials.quote", { content: l(review, "content") })}
			</blockquote>

			<figcaption className={style.by}>
				<div className={style.avatar}>
					{review.user?.safe_picture_url && (
						<img
							src={review.user.safe_picture_url}
							alt=""
							loading="lazy"
						/>
					)}
				</div>
				<div className={style.who}>
					<div className={style.name}>{review.user?.username}</div>
					{review.course?.name && (
						/* Plain text, not a link: the second copy of the rail
						   is aria-hidden, and a focusable link inside it would
						   be reachable by keyboard but invisible to a screen
						   reader. */
						<div className={style.course}>
							<Icon icon="fa-chalkboard" />
							<span>{l(review.course, "name")}</span>
						</div>
					)}
				</div>
			</figcaption>
		</figure>
	);
}

function TestimonialMarquee() {
	const { t } = useTranslation();
	const { reviews } = useFeaturedReviews();

	const usable = (reviews ?? []).filter((r) => r?.content?.trim());
	if (usable.length === 0) return null;

	const heading = (
		<div className={style.head}>
			<h2 className={style.title}>{t("landing:testimonials.title")}</h2>
		</div>
	);

	// One or two reviews cannot fill the rail, so the loop would visibly
	// stutter halfway through. Lay them out plainly instead.
	if (usable.length < 3) {
		return (
			<section className={style.section}>
				{heading}
				<div className={style.staticRow}>
					{usable.map((review) => (
						<Testimonial key={review.uuid} review={review} />
					))}
				</div>
			</section>
		);
	}

	return (
		<section className={style.section}>
			{heading}

			<div className={style.rail}>
				<div className={style.fadeLeft}></div>
				<div className={style.fadeRight}></div>
				<div className={style.track}>
					<div className={style.group}>
						{usable.map((review) => (
							<Testimonial key={review.uuid} review={review} />
						))}
					</div>
					{/* The duplicate exists only so the loop can seam; without
					    this, assistive tech reads every testimonial twice. */}
					<div className={style.group} aria-hidden="true">
						{usable.map((review) => (
							<Testimonial
								key={`${review.uuid}-copy`}
								review={review}
							/>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

export default TestimonialMarquee;
